const YOUTUBE_API_ENDPOINT = 'https://www.googleapis.com/youtube/v3/search';
const PIPED_ENDPOINT = 'https://piped.video/api/v1/search';

const youtubeApiKey = import.meta.env.VITE_YOUTUBE_API_KEY;
const youtubeProxy = import.meta.env.VITE_YOUTUBE_SEARCH_PROXY_URL;

export interface YoutubeVideoSuggestion {
  id: string;
  title: string;
  url: string;
  thumbnail: string;
  channel: string;
  duration?: string;
}

export async function suggestYoutubeVideo(query: string): Promise<YoutubeVideoSuggestion | null> {
  if (!query.trim()) {
    return null;
  }

  if (youtubeProxy) {
    const response = await fetch(`${youtubeProxy}?q=${encodeURIComponent(query)}`);
    if (!response.ok) {
      throw new Error('Failed to fetch YouTube suggestion from proxy');
    }
    const payload = await response.json();
    return normaliseProxyResult(payload);
  }

  if (youtubeApiKey) {
    const response = await fetch(
      `${YOUTUBE_API_ENDPOINT}?part=snippet&type=video&maxResults=1&q=${encodeURIComponent(query)}&key=${youtubeApiKey}`
    );
    if (!response.ok) {
      throw new Error('Failed to search YouTube');
    }
    const payload = await response.json();
    const item = payload.items?.[0];
    if (!item) {
      return null;
    }
    return {
      id: item.id.videoId,
      title: item.snippet.title,
      url: `https://www.youtube.com/watch?v=${item.id.videoId}`,
      thumbnail: item.snippet.thumbnails?.high?.url ?? item.snippet.thumbnails?.medium?.url ?? '',
      channel: item.snippet.channelTitle,
    };
  }

  const response = await fetch(`${PIPED_ENDPOINT}?q=${encodeURIComponent(query)}&region=US`);
  if (!response.ok) {
    throw new Error('Failed to search for YouTube fallback');
  }

  type PipedItem = {
    id?: string;
    videoId?: string;
    url?: string;
    title?: string;
    thumbnail?: string;
    thumbnails?: string[];
    uploader?: string;
    channelName?: string;
    duration?: string;
    type?: string;
  };

  type PipedResponse = {
    items?: PipedItem[];
  };

  const payload = (await response.json()) as PipedResponse | PipedItem[];
  const items = Array.isArray((payload as PipedResponse).items)
    ? (payload as PipedResponse).items
    : Array.isArray(payload)
    ? (payload as PipedItem[])
    : [];

  const item = items.find((entry) => entry.type === 'video') ?? items[0] ?? null;

  if (!item) {
    return null;
  }

  const id = item.id ?? item.url?.split('watch?v=')[1] ?? item.videoId;
  if (!id) {
    return null;
  }

  return {
    id,
    title: item.title ?? '',
    url: item.url ?? `https://www.youtube.com/watch?v=${id}`,
    thumbnail: item.thumbnail ?? item.thumbnails?.[0] ?? '',
    channel: item.uploader ?? item.channelName ?? '',
    duration: item.duration,
  };
}

function normaliseProxyResult(payload: unknown): YoutubeVideoSuggestion | null {
  if (!payload) return null;

  if (Array.isArray(payload)) {
    const first = payload[0] as Record<string, unknown> | undefined;
    if (!first) return null;
    const id = String(first.id ?? first.videoId ?? '');
    if (!id) return null;
    return {
      id,
      title: typeof first.title === 'string' ? first.title : '',
      url: typeof first.url === 'string' ? first.url : `https://www.youtube.com/watch?v=${id}`,
      thumbnail: typeof first.thumbnail === 'string' ? first.thumbnail : '',
      channel: typeof first.channel === 'string' ? first.channel : '',
    };
  }

  if (typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const rawId = record.videoId ?? record.id;
    if (!rawId || typeof rawId !== 'string') {
      return null;
    }
    return {
      id: rawId,
      title: typeof record.title === 'string' ? record.title : '',
      url:
        typeof record.url === 'string'
          ? record.url
          : `https://www.youtube.com/watch?v=${rawId}`,
      thumbnail: typeof record.thumbnail === 'string' ? record.thumbnail : '',
      channel:
        typeof record.channel === 'string'
          ? record.channel
          : typeof record.channelTitle === 'string'
          ? record.channelTitle
          : '',
    };
  }
  return null;
}

